import { useEffect, useMemo, useState, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Globe2, GitCompare, Percent, Newspaper, Info, Youtube, Mic, Plane, User } from "lucide-react";
import { Toaster, toast } from "sonner";
import Globe from "@/components/Globe";
import CountrySheet from "@/components/CountrySheet";
import ServerJourneyView from "@/components/ServerJourneyView";
import QualificationStep from "@/components/QualificationStep";
import MyWorld from "@/components/MyWorld";
import CustomerAuth from "@/components/CustomerAuth";
import CoordinationSheet from "@/components/CoordinationSheet";
import CompareView from "@/components/CompareView";
import { AdviserFab, AdviserConcierge } from "@/components/AdviserConcierge";
import LeadForm from "@/components/LeadForm";
import AboutPage from "@/components/AboutPage";
import BlogPage from "@/components/BlogPage";
import VideosPage from "@/components/VideosPage";
import PodcastPage from "@/components/PodcastPage";
import { AdminButton } from "@/components/AdminPanel";
import { useAuth } from "@/lib/auth";
import { useCustomer, capi } from "@/lib/customer";
import { CATEGORIES } from "@/lib/portal";
import {
  getState,
  toggleSaved,
  toggleCompare,
} from "@/lib/portal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function overlayOne(c) {
  if (!c.live) return c;
  const l = c.live;
  return {
    ...c,
    from_display: l.from_display || c.from_display,
    snapshot: {
      ...c.snapshot,
      minimum_investment: l.minimum_investment || c.snapshot.minimum_investment,
      typical_pathway: l.typical_pathway || c.snapshot.typical_pathway,
      residency_outcome: l.residency_outcome || c.snapshot.residency_outcome,
    },
    live: l,
  };
}
const overlay = (arr) => arr.map(overlayOne);

const TABS = [
  { id: "explore", label: "Explore", icon: Globe2, testid: "nav-tab-explore" },
  { id: "podcast", label: "Podcast", icon: Mic, testid: "nav-tab-podcast" },
  { id: "compare", label: "Compare", icon: GitCompare, testid: "nav-tab-compare" },
  { id: "blog", label: "Blog", icon: Newspaper, testid: "nav-tab-blog" },
  { id: "videos", label: "Videos", icon: Youtube, testid: "nav-tab-videos" },
  { id: "about", label: "About", icon: Info, testid: "nav-tab-about" },
];

export default function App() {
  const { isAdmin } = useAuth();
  const { customer } = useCustomer();
  const [countries, setCountries] = useState([]);
  const [mode, setMode] = useState("explore");
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null); // country in sheet
  const [focusCode, setFocusCode] = useState(null);
  // customer journey slice
  const [qualifyCountry, setQualifyCountry] = useState(null);
  const [building, setBuilding] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authReason, setAuthReason] = useState(null);
  const [activeJourney, setActiveJourney] = useState(null); // server journey being viewed
  const [myJourneys, setMyJourneys] = useState([]);
  const [myWorldKey, setMyWorldKey] = useState(0);
  const [coordinate, setCoordinate] = useState(null);
  const [visaMode, setVisaMode] = useState(false);
  const [visaOrigin, setVisaOrigin] = useState(null);
  const [visaArcs, setVisaArcs] = useState([]);

  const showVisaMap = useCallback((c) => {
    setFocusCode(c.code);
    axios.get(`${API}/visa-free/${c.code}`).then(({ data }) => {
      setVisaArcs((data.destinations || []).map((d) => [d.lng, d.lat]));
      setVisaOrigin({ code: c.code, name: c.name, flag: c.flag, count: data.count });
    }).catch(() => toast.error("Could not load visa-free data"));
  }, []);

  const enterVisaMode = (c) => { setSelected(null); setVisaMode(true); if (c) showVisaMap(c); };
  const exitVisaMode = () => { setVisaMode(false); setVisaOrigin(null); setVisaArcs([]); };
  const handleGlobeSelect = (c) => { if (visaMode) showVisaMap(c); else openCountry(c); };
  const [adviser, setAdviser] = useState({ open: false, tab: "message" });
  const [lead, setLead] = useState(null);
  const [state, setState] = useState(getState());
  const [compareSel, setCompareSel] = useState([null, null, null]);
  const [podcastSlug, setPodcastSlug] = useState(null);
  const [autoEditCode, setAutoEditCode] = useState(null);

  const fetchCountries = useCallback(() => {
    axios.get(`${API}/countries`).then((r) => setCountries(overlay(r.data))).catch(() => {});
  }, []);

  const openCountryForEdit = useCallback((c) => {
    const full = overlayOne(c);
    setMode("explore");
    setSelected(full);
    setAutoEditCode(c.code);
  }, []);

  const openPodcast = useCallback((slug) => {
    setPodcastSlug(slug);
    setMode("podcast");
  }, []);

  const refresh = useCallback(() => setState(getState()), []);

  useEffect(() => {
    axios.get(`${API}/countries`).then((r) => setCountries(overlay(r.data))).catch(() => {});
    window.addEventListener("gmjp_state", refresh);
    return () => window.removeEventListener("gmjp_state", refresh);
  }, [refresh]);

  const refreshCountry = async (code) => {
    const t = toast.loading("Fetching latest indicative data…");
    try {
      const r = await axios.post(`${API}/countries/${code}/refresh`);
      setCountries((prev) => prev.map((c) => (c.code === code ? overlayOne({ ...c, live: r.data }) : c)));
      setSelected((sel) => (sel && sel.code === code ? overlayOne({ ...sel, live: r.data }) : sel));
      toast.success("Latest data refreshed", { id: t });
    } catch {
      toast.error("Refresh failed — try again", { id: t });
    }
  };

  const onCountryUpdated = (updated) => {
    const merged = overlayOne(updated);
    setCountries((prev) => prev.map((c) => (c.code === updated.code ? merged : c)));
    setSelected((sel) => (sel && sel.code === updated.code ? merged : sel));
  };

  const filtered = useMemo(
    () => (filter === "All" ? countries : countries.filter((c) => c.categories?.includes(filter))),
    [countries, filter]
  );

  // When a category filter narrows the set, pan the globe to that cluster.
  useEffect(() => {
    if (filter !== "All" && filtered.length) setFocusCode(filtered[0].code);
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const statusMap = useMemo(() => {
    const m = {};
    myJourneys.forEach((j) => (m[j.country_code] = "active"));
    state.saved.forEach((c) => { if (!m[c]) m[c] = "saved"; });
    return m;
  }, [myJourneys, state]);

  const loadMyJourneys = useCallback(async () => {
    if (!customer) { setMyJourneys([]); return; }
    try {
      const { data } = await capi.get("/customer/journeys");
      setMyJourneys(data);
    } catch { /* ignore */ }
  }, [customer]);

  useEffect(() => { loadMyJourneys(); }, [loadMyJourneys]);

  const openCountry = (c) => {
    setSelected(c);
    setFocusCode(c.code);
  };

  const openJourney = (journey) => {
    setActiveJourney(journey);
    setMode("journey");
  };

  const createJourney = useCallback(async (code, answers, objective) => {
    setBuilding(true);
    try {
      const { data } = await capi.post("/customer/journeys", { country_code: code, answers, objective });
      setActiveJourney(data);
      setQualifyCountry(null);
      setMode("journey");
      setMyWorldKey((k) => k + 1);
      loadMyJourneys();
      toast.success("Your journey is ready");
    } catch {
      toast.error("Could not build your journey — please try again");
    } finally {
      setBuilding(false);
    }
  }, [loadMyJourneys]);

  // Explore → qualification questions
  const explorePathway = (c) => {
    setSelected(null);
    setQualifyCountry(c);
  };

  // Qualification → build journey (auth-gated)
  const buildJourney = (answers) => {
    if (!qualifyCountry) return;
    const objective = qualifyCountry.objective;
    if (!customer) {
      sessionStorage.setItem("gmjp_pending_journey", JSON.stringify({ code: qualifyCountry.code, answers, objective }));
      setAuthReason("Create your free account to save and track this journey.");
      setAuthOpen(true);
      return;
    }
    createJourney(qualifyCountry.code, answers, objective);
  };

  // Resume a pending journey after auth (modal or Google redirect)
  useEffect(() => {
    if (!customer) return;
    const raw = sessionStorage.getItem("gmjp_pending_journey");
    if (raw) {
      sessionStorage.removeItem("gmjp_pending_journey");
      try {
        const p = JSON.parse(raw);
        createJourney(p.code, p.answers, p.objective);
      } catch { /* ignore */ }
    }
    loadMyJourneys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const updateJourneyAnswer = async (key, value) => {
    if (!activeJourney) return;
    try {
      const { data } = await capi.patch(`/customer/journeys/${activeJourney.id}/answers`, { answers: { [key]: value } });
      setActiveJourney(data);
      setMyWorldKey((k) => k + 1);
    } catch { toast.error("Could not update"); }
  };

  const handleJourneyUpdate = (j) => {
    setActiveJourney(j);
    setMyWorldKey((k) => k + 1);
  };

  const openAccount = () => {
    if (customer) { setMode("myworld"); }
    else { setAuthReason(null); setAuthOpen(true); }
  };

  return (
    <div className="relative mx-auto flex min-h-screen max-w-lg flex-col bg-[#07090E] text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <Toaster position="top-center" theme="dark" richColors />

      {/* header */}
      <header className="sticky top-0 z-[45] flex items-center justify-between border-b border-white/[0.06] bg-[#07090E]/85 px-4 py-3 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <div className="rounded-lg bg-white px-2.5 py-1.5 shadow-sm">
            <img
              data-testid="brand-logo"
              src="https://customer-assets-lxgj4vgw.emergentagent.net/job_global-migrate-4/artifacts/70jhhwwv_gateway2world%20logo%20final.webp"
              alt="Gateway2World — Residency & Citizenship"
              className="h-7 w-auto"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="account-button"
            onClick={openAccount}
            aria-label={customer ? "My World" : "Sign in"}
            className="flex items-center gap-1.5 rounded-full border border-white/12 bg-white/[0.04] px-2.5 py-1.5 text-[11px] font-semibold text-slate-200 transition-colors hover:border-amber-300/40"
          >
            {customer ? (
              <>
                <span className="grid h-5 w-5 place-items-center rounded-full bg-amber-300/20 text-[10px] font-bold text-amber-200">
                  {(customer.name || "U").trim().charAt(0).toUpperCase()}
                </span>
                My World
              </>
            ) : (
              <><User size={13} /> Sign in</>
            )}
          </button>
          <AdminButton countries={countries} onDataChanged={fetchCountries} onEditCountry={openCountryForEdit} />
        </div>
      </header>

      {/* content */}
      <main className="flex flex-1 flex-col pb-20">
        {mode === "explore" && (
          <ExploreMode
            countries={filtered}
            filter={filter}
            setFilter={setFilter}
            focusCode={focusCode}
            spinPaused={!!selected}
            statusMap={statusMap}
            onSelect={handleGlobeSelect}
            visaMode={visaMode}
            visaOrigin={visaOrigin}
            visaArcs={visaArcs}
            onEnterVisa={enterVisaMode}
            onExitVisa={exitVisaMode}
          />
        )}
        {mode === "journey" && (
          <ServerJourneyView
            journey={activeJourney}
            onBack={() => setMode("myworld")}
            onUpdateAnswer={updateJourneyAnswer}
            onJourneyUpdate={handleJourneyUpdate}
            onCoordinate={(j) => setCoordinate(j)}
          />
        )}
        {mode === "myworld" && (
          <MyWorld
            reloadKey={myWorldKey}
            onOpenJourney={openJourney}
            onExplore={() => setMode("explore")}
          />
        )}
        {mode === "podcast" && <PodcastPage initialSlug={podcastSlug} />}
        {mode === "compare" && (
          <CompareView countries={countries} selected={compareSel} setSelected={setCompareSel} />
        )}
        {mode === "blog" && <BlogPage onListen={openPodcast} />}
        {mode === "videos" && <VideosPage />}
        {mode === "about" && <AboutPage onContact={() => setAdviser({ open: true, tab: "message" })} />}
      </main>

      {/* persistent adviser */}
      <div data-testid="persistent-global-adviser-card">
        <AdviserFab onOpen={() => setAdviser({ open: true, tab: "message" })} />
      </div>
      <AdviserConcierge
        open={adviser.open}
        initialTab={adviser.tab}
        onClose={() => setAdviser({ open: false, tab: "message" })}
        key={adviser.tab + adviser.open}
      />

      {/* bottom nav */}
      <nav className="fixed inset-x-0 bottom-0 z-30 mx-auto flex max-w-lg items-center justify-around border-t border-white/[0.07] bg-[#07090E]/95 px-2 py-2 backdrop-blur-md">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = mode === t.id;
          return (
            <button
              key={t.id}
              data-testid={t.testid}
              onClick={() => { if (t.id === "podcast") setPodcastSlug(null); setMode(t.id); }}
              className="flex flex-1 flex-col items-center gap-0.5 py-1 text-[10px] font-medium transition-colors"
              style={{ color: active ? "#E2C275" : "#94A3B8" }}
            >
              <Icon size={20} strokeWidth={active ? 2.4 : 1.8} />
              {t.label}
            </button>
          );
        })}
      </nav>

      {/* country snapshot sheet */}
      <CountrySheet
        country={selected}
        admin={isAdmin}
        onCountryUpdated={onCountryUpdated}
        autoEditCode={autoEditCode}
        onAutoEditConsumed={() => setAutoEditCode(null)}
        saved={selected ? state.saved.includes(selected.code) : false}
        comparing={selected ? state.compare.includes(selected.code) : false}
        onClose={() => setSelected(null)}
        onExplore={explorePathway}
        onRefresh={refreshCountry}
        onViewVisaMap={enterVisaMode}
        onSave={(c) => { toggleSaved(c.code); refresh(); }}
        onCompare={(c) => { toggleCompare(c.code); refresh(); toast(`${c.name} on compare list`); }}
        onAsk={() => { setSelected(null); setAdviser({ open: true, tab: "ask" }); }}
      />

      <LeadForm country={lead?.country} services={lead?.services} onClose={() => setLead(null)} />

      {/* qualification questions → build journey */}
      <QualificationStep
        country={qualifyCountry}
        building={building}
        onClose={() => setQualifyCountry(null)}
        onBuild={buildJourney}
      />

      {/* customer auth (email/password + Google) */}
      <CustomerAuth
        open={authOpen}
        reason={authReason}
        onClose={() => setAuthOpen(false)}
        onAuthed={() => {
          setAuthOpen(false);
          if (!sessionStorage.getItem("gmjp_pending_journey")) setMode("myworld");
        }}
      />

      {/* coordination request with full assessment → consultant */}
      <CoordinationSheet journey={coordinate} onClose={() => setCoordinate(null)} />
    </div>
  );
}

function ExploreMode({ countries, filter, setFilter, focusCode, spinPaused, statusMap, onSelect, visaMode, visaOrigin, visaArcs, onEnterVisa, onExitVisa }) {
  // Tight clusters (e.g. Caribbean) zoom in so the islands spread out.
  const zoom = (() => {
    if (filter === "All" || countries.length < 2) return 1;
    const lats = countries.map((c) => c.lat);
    const lngs = countries.map((c) => c.lng);
    const spread = Math.max(Math.max(...lats) - Math.min(...lats), Math.max(...lngs) - Math.min(...lngs));
    if (spread < 30) return 1.9;
    if (spread < 70) return 1.5;
    return 1.2;
  })();
  return (
    <div className="flex flex-1 flex-col">
      {/* hero title (above globe, never overlapped) */}
      <div className="px-5 pb-1 pt-4 text-center">
        <h1 className="text-2xl font-light leading-snug tracking-tight sm:text-3xl" style={{ fontFamily: "'Syne', sans-serif" }} data-testid="hero-title">
          Global residency isn't about choosing a country. It's about choosing the <span className="font-semibold text-amber-200">right strategy</span>.
        </h1>
        <p className="mx-auto mt-1.5 max-w-xs text-xs text-slate-300">
          Explore opportunities to live, invest and build your future around the world.
        </p>
      </div>

      {/* hero globe */}
      <div className="relative h-[46vh] min-h-[340px] w-full overflow-hidden">
        <Globe
          countries={countries}
          onSelect={onSelect}
          focusCode={focusCode}
          spinPaused={spinPaused || visaMode}
          statusMap={statusMap}
          zoom={zoom}
          visaMode={visaMode}
          arcs={visaArcs}
          arcOrigin={visaOrigin && countries.find((c) => c.code === visaOrigin.code) ? [countries.find((c) => c.code === visaOrigin.code).lng, countries.find((c) => c.code === visaOrigin.code).lat] : null}
        />

        {/* visa-free travel toggle */}
        <button
          data-testid="visa-mode-toggle"
          onClick={() => (visaMode ? onExitVisa() : onEnterVisa(countries[0]))}
          className="absolute left-3 top-3 z-10 flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold backdrop-blur-md transition-colors"
          style={{
            borderColor: visaMode ? "rgba(56,189,248,0.6)" : "rgba(255,255,255,0.14)",
            background: visaMode ? "rgba(56,189,248,0.14)" : "rgba(10,14,23,0.7)",
            color: visaMode ? "#7dd3fc" : "#CBD5E1",
          }}
        >
          <Plane size={13} /> {visaMode ? "Exit visa-free view" : "Visa-free travel"}
        </button>

        {visaMode && (
          <div data-testid="visa-caption" className="absolute bottom-3 left-3 z-10 max-w-[62%] rounded-xl border border-sky-400/25 bg-[#0B0F17]/90 px-3 py-2 backdrop-blur-md">
            {visaOrigin ? (
              <>
                <p className="text-[13px] font-semibold text-white">{visaOrigin.flag} {visaOrigin.name}</p>
                <p className="text-[11px] text-sky-300"><span className="font-bold">{visaOrigin.count}</span> visa-free destinations</p>
              </>
            ) : (
              <p className="text-[11px] text-slate-300">Tap any destination to see its visa-free reach.</p>
            )}
            <p data-testid="visa-travel-disclaimer" className="mt-1.5 border-t border-white/10 pt-1.5 text-[10px] leading-snug text-slate-400">
              For details about travel information, please discuss with your Consultant.
            </p>
          </div>
        )}
      </div>

      {/* filters */}
      <div className="no-scrollbar flex gap-2 overflow-x-auto px-4 py-3">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            data-testid={`filter-${cat.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => setFilter(cat)}
            className="shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors"
            style={{
              borderColor: filter === cat ? "rgba(226,194,117,0.5)" : "rgba(255,255,255,0.08)",
              color: filter === cat ? "#E2C275" : "#94A3B8",
              background: filter === cat ? "rgba(226,194,117,0.1)" : "transparent",
            }}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* curated destinations */}
      <div className="px-4 pb-6">
        <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400/90">Curated Destinations</p>
        <div className="grid grid-cols-2 gap-3">
          {countries.map((c) => (
            <button
              key={c.code}
              data-testid={`destination-card-${c.code}`}
              onClick={() => onSelect(c)}
              className="group overflow-hidden rounded-2xl border border-white/[0.07] p-4 text-left transition-all hover:border-amber-300/30"
              style={{ background: `linear-gradient(135deg, ${c.accent}18, rgba(13,17,26,0.5))` }}
            >
              <div className="flex items-center justify-between">
                <span className="text-3xl">{c.flag}</span>
                {statusMap[c.code] === "active" ? (
                  <span className="rounded-full bg-emerald-400/15 px-2 py-0.5 text-[9px] font-semibold text-emerald-300">ACTIVE</span>
                ) : statusMap[c.code] === "saved" ? (
                  <span className="rounded-full bg-amber-300/15 px-2 py-0.5 text-[9px] font-semibold text-amber-300">SAVED</span>
                ) : (
                  <span
                    className="rounded-full px-2 py-0.5 text-[9px] font-semibold"
                    style={{
                      background: c.type === "Citizenship" ? "rgba(52,211,153,0.14)" : "rgba(148,163,184,0.14)",
                      color: c.type === "Citizenship" ? "#34D399" : "#94A3B8",
                    }}
                  >
                    {c.type === "Citizenship" ? "CITIZENSHIP" : "RESIDENCY"}
                  </span>
                )}
              </div>
              <p className="mt-2 text-sm font-semibold text-white">{c.name}</p>
              <p className="text-[10px] uppercase tracking-wider text-slate-400">{c.region}</p>
              <p className="mt-0.5 text-[11px] text-slate-300">{c.program}</p>
              <p className="mt-1.5 font-mono text-xs font-semibold text-amber-200">{c.from_display}</p>
              <p className="mt-1 flex items-center gap-1 text-[10px] text-emerald-300/90" data-testid={`card-tax-${c.code}`}>
                <Percent size={10} /> {(c.tax_profile || "").split("•")[0].trim()}
              </p>
              {c.live && (
                <span className="mt-1.5 flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-emerald-400">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" /> Live
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
